import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import './NewClientPage.css'; // Import the CSS file

function NewClientPage() {
  const location = useLocation();
  const [validationStatus, setValidationStatus] = useState('validating'); // validating, valid, invalid
  const [inviteCodeData, setInviteCodeData] = useState(null);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // State for submission loading
  const [submissionStatus, setSubmissionStatus] = useState('idle'); // idle, submitting, success, error

  // State for the onboarding form
  const [onboardingData, setOnboardingData] = useState({
    company_name: '',
    accounting_software_use: '',
    accounting_software_name: '',
    add_team_to_software: '',
    setup_on_our_system: '',
    bank_accounts_count: '',
    report_frequency: '',
    payroll_provider: '',
    invoicing_help: '',
    report_email: '',
    payroll_provider_name: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOnboardingData({ ...onboardingData, [name]: value });
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    if (name === 'payroll_provider' && value !== 'Yes') {
      setOnboardingData(prevState => ({
        ...prevState,
        [name]: value,
        payroll_provider_name: '',
      }));
    } else {
      setOnboardingData({ ...onboardingData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return; // Prevent multiple submissions
    }

    setIsSubmitting(true);
    setSubmissionStatus('submitting');
    setError(null); // Clear previous errors

    // Ensure inviteCodeData and inviteCode exist
    if (!inviteCodeData || !inviteCodeData.invite_code) {
      setSubmissionStatus('error');
      setError('Invite code data is missing.');
      setIsSubmitting(false);
      return;
    }

    const inviteCode = inviteCodeData.invite_code;

    try {
      // Send the onboarding data to the backend endpoint
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/invite-codes/${inviteCode}/submit-onboarding`,
        onboardingData
      );

      console.log('Submission successful:', response.data);
      setSubmissionStatus('success');
      // Optionally clear the form or redirect the user
      // setOnboardingData({ ...initial empty state ...});

    } catch (err) {
      console.error('Error submitting onboarding data:', err);
      setSubmissionStatus('error');
      if (err.response && err.response.data && err.response.data.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Failed to submit onboarding data.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');

    if (!code) {
      setValidationStatus('invalid');
      setError('No invite code found in URL.');
      return;
    }

    const validateCode = async () => {
      try {
        // Adjust the URL if your backend is on a different host/port
        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/invite-codes/${code}`);
        setInviteCodeData(response.data);
        setValidationStatus('valid');
      } catch (err) {
        console.error('Error validating invite code:', err);
        setValidationStatus('invalid');
        if (err.response && err.response.data && err.response.data.detail) {
          setError(err.response.data.detail);
        } else {
          setError('Failed to validate invite code.');
        }
      }
    };

    validateCode();
  }, [location.search]);

  return (
    <div className="new-client-page">
      <h1>New Client On-boarding</h1>
      {
        validationStatus === 'validating' && (
          <p className="validation-loading">Validating invite code</p>
        )
      }
      {
        validationStatus === 'valid' && inviteCodeData && (
          <>
            <h2>Bookkeeping Client Onboarding Questionnaire</h2>
            <form onSubmit={handleSubmit} className="new-client-form">
              {/* Question 1 */}
              <div className="new-client-form-group">
                <label htmlFor="company_name">What's your company name?</label>
                <input
                  type="text"
                  id="company_name"
                  name="company_name"
                  value={onboardingData.company_name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* Question 2 */}
              <div className="new-client-form-group">
                <p>Do you currently use any accounting software (e.g., QuickBooks, Xero, etc.)?</p>
                <label>
                  <input
                    type="radio"
                    name="accounting_software_use"
                    value="Yes"
                    checked={onboardingData.accounting_software_use === 'Yes'}
                    onChange={handleRadioChange}
                    required
                  /> Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="accounting_software_use"
                    value="No"
                    checked={onboardingData.accounting_software_use === 'No'}
                    onChange={handleRadioChange}
                    required
                  /> No
                </label>
              </div>

              {/* Question 3 (Conditional) */}
              {onboardingData.accounting_software_use === 'Yes' && (
                <div className="new-client-form-group">
                  <label htmlFor="accounting_software_name">If yes, which one?</label>
                  <input
                    type="text"
                    id="accounting_software_name"
                    name="accounting_software_name"
                    value={onboardingData.accounting_software_name}
                    onChange={handleInputChange}
                  />
                </div>
              )}

              {/* Question 4 (Conditional based on Q2 Yes) */}
              {onboardingData.accounting_software_use === 'Yes' && (
                <div className="new-client-form-group">
                  <p>Will you be able to add our team to your accounting software?</p>
                  <label>
                    <input
                      type="radio"
                      name="add_team_to_software"
                      value="Yes"
                      checked={onboardingData.add_team_to_software === 'Yes'}
                      onChange={handleRadioChange}
                      required
                    /> Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="add_team_to_software"
                      value="No"
                      checked={onboardingData.add_team_to_software === 'No'}
                      onChange={handleRadioChange}
                      required
                    /> No
                  </label>
                   <label>
                    <input
                      type="radio"
                      name="add_team_to_software"
                      value="Not sure"
                      checked={onboardingData.add_team_to_software === 'Not sure'}
                      onChange={handleRadioChange}
                      required
                    /> Not sure
                  </label>
                </div>
              )}

              {/* Question 5 (Conditional based on Q2 No) */}
              {onboardingData.accounting_software_use === 'No' && (
                 <div className="new-client-form-group">
                  <p>If you don't use one, would you like us to set you up on our system?</p>
                  <label>
                    <input
                      type="radio"
                      name="setup_on_our_system"
                      value="Yes"
                      checked={onboardingData.setup_on_our_system === 'Yes'}
                      onChange={handleRadioChange}
                      required
                    /> Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="setup_on_our_system"
                      value="No"
                      checked={onboardingData.setup_on_our_system === 'No'}
                      onChange={handleRadioChange}
                      required
                    /> No
                  </label>
                   <label>
                    <input
                      type="radio"
                      name="setup_on_our_system"
                      value="Need more info"
                      checked={onboardingData.setup_on_our_system === 'Need more info'}
                      onChange={handleRadioChange}
                      required
                    /> Need more info
                  </label>
                </div>
              )}

              {/* Question 6 */}
               <div className="new-client-form-group">
                <label htmlFor="bank_accounts_count">How many bank or credit card accounts do you want us to reconcile monthly?</label>
                <input
                  type="text"
                  id="bank_accounts_count"
                  name="bank_accounts_count"
                  value={onboardingData.bank_accounts_count}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* Question 7 */}
               <div className="new-client-form-group">
                <p>How often do you need reports (e.g., profit/loss, balance sheet)?</p>
                <label>
                  <input
                    type="radio"
                    name="report_frequency"
                    value="Monthly"
                    checked={onboardingData.report_frequency === 'Monthly'}
                    onChange={handleRadioChange}
                    required
                  /> Monthly
                </label>
                <label>
                  <input
                    type="radio"
                    name="report_frequency"
                    value="Quarterly"
                    checked={onboardingData.report_frequency === 'Quarterly'}
                    onChange={handleRadioChange}
                    required
                  /> Quarterly
                </label>
                 <label>
                  <input
                    type="radio"
                    name="report_frequency"
                    value="Not sure"
                    checked={onboardingData.report_frequency === 'Not sure'}
                    onChange={handleRadioChange}
                    required
                  /> Not sure
                </label>
              </div>

              {/* Question 8 */}
               <div className="new-client-form-group">
                <p>Do you currently have a payroll provider?</p>
                 <label>
                  <input
                    type="radio"
                    name="payroll_provider"
                    value="Yes"
                    checked={onboardingData.payroll_provider === 'Yes'}
                    onChange={handleRadioChange}
                    required
                  /> Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="payroll_provider"
                    value="No"
                    checked={onboardingData.payroll_provider === 'No'}
                    onChange={handleRadioChange}
                    required
                  /> No
                </label>
                 <label>
                  <input
                    type="radio"
                    name="payroll_provider"
                    value="Will need one"
                    checked={onboardingData.payroll_provider === 'Will need one'}
                    onChange={handleRadioChange}
                    required
                  /> Will need one
                </label>
              </div>

              {/* Conditional Payroll Provider Name Input */}
              {onboardingData.payroll_provider === 'Yes' && (
                 <div className="new-client-form-group">
                  <label htmlFor="payroll_provider_name">Please specify your payroll provider:</label>
                  <input
                    type="text"
                    id="payroll_provider_name"
                    name="payroll_provider_name"
                    value={onboardingData.payroll_provider_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              )}

              {/* Question 9 */}
               <div className="new-client-form-group">
                <p>Would you like help with invoicing and bill payments?</p>
                 <label>
                  <input
                    type="radio"
                    name="invoicing_help"
                    value="Yes"
                    checked={onboardingData.invoicing_help === 'Yes'}
                    onChange={handleRadioChange}
                    required
                  /> Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="invoicing_help"
                    value="No"
                    checked={onboardingData.invoicing_help === 'No'}
                    onChange={handleRadioChange}
                    required
                  /> No
                </label>
                 <label>
                  <input
                    type="radio"
                    name="invoicing_help"
                    value="Maybe later"
                    checked={onboardingData.invoicing_help === 'Maybe later'}
                    onChange={handleRadioChange}
                    required
                  /> Maybe later
                </label>
              </div>

              {/* Question 10 */}
               <div className="new-client-form-group">
                <label htmlFor="report_email">What's the best email to send your reports and updates to?</label>
                <input
                  type="email"
                  id="report_email"
                  name="report_email"
                  value={onboardingData.report_email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Onboarding Information'}
              </button>
            </form>

            {submissionStatus === 'success' && (
              <div className="status-message status-success">
                <p>Onboarding information submitted successfully! Thank you for providing your details.</p>
                <p>Our team will review your information and contact you shortly to discuss next steps.</p>
              </div>
            )}

            {submissionStatus === 'error' && (
              <div className="status-message status-error">
                <p>Submission failed.</p>
                {error && <p>Details: {error}</p>}
                <p>Please contact support at <a href="mailto:support@rkubeservices.com">support@rkubeservices.com</a> for assistance.</p>
              </div>
            )}
          </>
        )
      }
      {
        validationStatus === 'invalid' && (
          <div className="status-message status-error">
            <p>Invalid or expired invite code.</p>
            {error && <p>Details: {error}</p>}
            <p>Please contact support at <a href="mailto:support@rkubeservices.com">support@rkubeservices.com</a> for assistance.</p>
          </div>
        )
      }
    </div>
  );
}

export default NewClientPage; 